import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface EmptyStateFilter {
  key: string;
  value: string;
  label: string;
  isMulti: boolean;
  /** Human-readable text when `value` is an opaque id (e.g. colour). */
  display?: string;
  /** Optional swatch hex, rendered as a dot before the chip text. */
  swatch?: string | null;
}

interface SearchEmptyStateProps {
  /** Mode B when true (typed location or "Search this area"); Mode A when false. */
  locationActive: boolean;
  /** Whether the current zero-result query is scoped by the implicit browse-country anchor. */
  implicitLocationActive: boolean;
  /** Whether a map bounding box is currently applied ("Search this area"). */
  hasBounds: boolean;
  /** Active refinement filters (everything except the location/radius intent). */
  narrowingFilters: EmptyStateFilter[];
  /** Whether there is any active filter at all (incl. location) — drives "Clear all". */
  hasAnyActiveFilters: boolean;
  onRemoveFilter: (key: string, value: string, isMulti: boolean) => void;
  /** Clears the location intent (location + bounds) but keeps filters → Mode A. */
  onShowAnywhere: () => void;
  /** Opts out of the implicit browse-country anchor and searches globally. */
  onSearchEverywhere: () => void;
  /** Clears the map bounding box (and returns to Mode A). */
  onResetArea: () => void;
  /** Clears every filter and the location. */
  onClearAll: () => void;
}

/**
 * Location-aware "no results" state. Shared by both search
 * paths (Algolia + Supabase) so the behaviour is identical:
 *
 *  - Mode A (no active location): the only way to be empty is a genuinely
 *    zero-match catalogue query, so just offer "Clear all filters".
 *  - Mode B (active location) WITH narrowing filters: name the offending
 *    filter(s), let the user drop each one individually, and offer a
 *    "Show <filter> anywhere" escape that drops the location intent and
 *    switches to the nationwide Mode A view.
 *  - Mode B with no narrowing filters (the area itself is empty): fall
 *    back to the plain "no cars in this area" message.
 */
export function SearchEmptyState({
  locationActive,
  implicitLocationActive,
  hasBounds,
  narrowingFilters,
  hasAnyActiveFilters,
  onRemoveFilter,
  onShowAnywhere,
  onSearchEverywhere,
  onResetArea,
  onClearAll,
}: SearchEmptyStateProps) {
  const { t } = useTranslation();

  const hasNarrowing = narrowingFilters.length > 0;
  const smart = locationActive && hasNarrowing;
  const implicitEmpty = !locationActive && implicitLocationActive;

  const message = smart
    ? t("search.no_cars_in_area_filtered")
    : locationActive
    ? t("search.no_cars_in_area")
    : implicitEmpty
    ? t("search.no_cars_near_you")
    : t("search.try_adjusting_filters");

  const showAnywhereLabel =
    narrowingFilters.length === 1
      ? t("search.show_filter_anywhere", {
          filter: narrowingFilters[0].display || narrowingFilters[0].value,
        })
      : t("search.show_anywhere_all");

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
      <Search className="h-12 w-12 text-muted-foreground/30" />
      <h3 className="mt-4 text-lg font-bold text-foreground">{t("search.no_cars_found")}</h3>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{message}</p>

      {smart && (
        <div className="mt-4 flex max-w-md flex-wrap items-center justify-center gap-2">
          {narrowingFilters.map((f, i) => (
            <span
              key={`${f.key}-${f.value}-${i}`}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
            >
              {f.swatch && (
                <span
                  className="h-3 w-3 shrink-0 rounded-full border border-border"
                  style={{ backgroundColor: f.swatch }}
                />
              )}
              {f.label}: {f.display || f.value}
              <button
                type="button"
                onClick={() => onRemoveFilter(f.key, f.value, f.isMulti)}
                className="ml-0.5 hover:text-primary"
                aria-label={t("search.remove_filter", { filter: f.display || f.value })}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {implicitEmpty && (
          <button
            type="button"
            onClick={onSearchEverywhere}
            className="primary-action cursor-pointer rounded-full px-6 py-2 text-sm font-semibold"
          >
            {t("search.near_you_change")}
          </button>
        )}
        {smart && (
          <button
            type="button"
            onClick={onShowAnywhere}
            className="primary-action cursor-pointer rounded-full px-6 py-2 text-sm font-semibold"
          >
            {showAnywhereLabel}
          </button>
        )}
        {hasBounds && (
          <button
            type="button"
            onClick={onResetArea}
            className="cursor-pointer rounded-full border border-border bg-background px-6 py-2 text-sm font-semibold text-foreground hover:bg-muted"
          >
            {t("common.reset_area")}
          </button>
        )}
        {hasAnyActiveFilters && (
          <button
            type="button"
            onClick={onClearAll}
            className="cursor-pointer rounded-full border border-border bg-background px-6 py-2 text-sm font-semibold text-foreground hover:bg-muted"
          >
            {t("common.clear_all_filters")}
          </button>
        )}
      </div>
    </div>
  );
}
